import { useState, useEffect } from 'react';
import { useAuth } from '../AuthProvider';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface TrialStatus {
  isActive: boolean;
  endsAt: string | null;
  daysLeft: number;
  hasCompletedTrial: boolean;
}

interface TrialResponse {
  trial: TrialStatus;
  subscription: {
    plan: string;
    status: string;
  };
  hasFullAccess: boolean;
}

export const useTrial = () => {
  const { session } = useAuth();
  const [trialStatus, setTrialStatus] = useState<TrialResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTrialStatus = async () => {
    if (!session?.access_token) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/account/trial-status`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch trial status');
      }

      const data = await response.json();
      setTrialStatus(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching trial status:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrialStatus();
    
    // Refresh trial status every minute
    const interval = setInterval(fetchTrialStatus, 60000);
    
    return () => clearInterval(interval);
  }, [session]);

  return {
    trialStatus,
    loading,
    error,
    refetch: fetchTrialStatus,
    hasFullAccess: trialStatus?.hasFullAccess || false,
    isTrialActive: trialStatus?.trial?.isActive || false,
    daysLeft: trialStatus?.trial?.daysLeft || 0,
    needsPayment: trialStatus && !trialStatus.hasFullAccess,
  };
}; 